import React, { useState, useEffect } from "react";
import FlagIcon from "../components/FlagIcon";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Grid,
} from "@mui/material";
import nationsBanner from "../img/nationsBanner.png";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

interface NationsLeagueCountry {
  name: string;
  isoCode: string;
  group: "A" | "B" | "C";
}

interface NationsLeagueEvent {
  id: string;
  name: string;
  status: "active" | "completed";
  countries: NationsLeagueCountry[];
}

const getFlagEmoji = (isoCode: string): React.ReactNode => <FlagIcon flag={isoCode} />;

const NationsLeaguePublicList: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<NationsLeagueEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API_URL}/api/nations-league/`)
      .then((res) => setEvents(Array.isArray(res.data) ? res.data : []))
      .catch(() => setError("Failed to load Nations League editions"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: "bold" }}>
        🏆 Cup of Nations
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : events.length === 0 ? (
        <Alert severity="info">No Nations League editions available yet.</Alert>
      ) : (
        <Grid container spacing={3}>
          {events.map((event) => (
            <Grid item xs={12} md={6} key={event.id}>
              <Card
                onClick={() => navigate(`/cupOfNations/${event.id}`)}
                sx={{
                  cursor: "pointer",
                  height: "100%",
                  background: `linear-gradient(rgba(45, 53, 97, 0.9), rgba(26, 31, 58, 0.95)), url(${nationsBanner})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  color: "white",
                  position: "relative",
                  overflow: "hidden",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  filter: "grayscale(0.7) contrast(1.1)",
                  "&:hover": {
                    transform: "translateY(-8px) scale(1.02)",
                    boxShadow: "0 20px 60px rgba(80, 90, 140, 0.4)",
                    filter: "grayscale(0.6) contrast(1.1)",
                  },
                }}
              >
                <CardContent sx={{ position: "relative", zIndex: 1, p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Chip
                      label={event.status === "active" ? "ACTIVE" : "COMPLETED"}
                      sx={{
                        background:
                          event.status === "active"
                            ? "rgba(255, 193, 7, 0.9)"
                            : "rgba(76, 175, 80, 0.9)",
                        color: "#ffffff",
                        fontWeight: "bold",
                        fontSize: "0.75rem",
                      }}
                    />
                    <Box sx={{ fontSize: "2rem" }}>🏆</Box>
                  </Box>

                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: "bold",
                      mb: 2,
                      textShadow: "2px 2px 8px rgba(0,0,0,0.8)",
                    }}
                  >
                    {event.name}
                  </Typography>

                  <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
                    {(event.countries?.length || 8)} Nations fighting for first
                    place to become {event.name} champions
                  </Typography>

                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2, fontSize: "1.75rem" }}>
                    {(event.countries || []).map((c, idx) => (
                      <span key={idx} title={c.name}>
                        {getFlagEmoji(c.isoCode)}
                      </span>
                    ))}
                  </Box>

                  <Box
                    sx={{
                      mt: 3,
                      pt: 2,
                      borderTop: "1px solid rgba(255, 255, 255, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="body2" sx={{ opacity: 0.8, fontWeight: "medium" }}>
                      Click to view full standings
                    </Typography>
                    <Box sx={{ fontSize: "1.5rem" }}>→</Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default NationsLeaguePublicList;
